import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * The cached recommendations, on `window` object
 * @type {Record<string, string>}
 */
window.ProductRecommendations ||= {}
window.ProductRecommendations.cachedRecommendations ||= {};

class ProductRecommendations extends HTMLElement {
  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    if (this.closest('.sn_main_product_modal_post_add')) {
      document.addEventListener(ThemeEvents.cartUpdate, this.#loadRecommendations.bind(this));
      return;
    }

    this.#loadRecommendations();
  }

  disconnectedCallback() {}

  /**
   * Load the product recommendations
   * @param {CartUpdateEvent | null | undefined} cartUpdateEvent
   */
  #loadRecommendations(cartUpdateEvent) {
    const { productId, variantId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (cartUpdateEvent) {
      if (!cartUpdateEvent.detail?.add) return;
      if (cartUpdateEvent?.detail?.sourceId !== variantId) return;

      this.#continueLoading(productId, recommendationsPerformed, sectionId, intent, id, true);
      return;
    }

    // capisco se ho prodotti
    fetch(window.Shopify.routes.root + `recommendations/products.json?product_id=${productId}&limit=1&intent=${intent}`)
      .then(response => response.json())
      .then(({ products }) => {
        if (products.length === 0) {
          this.classList.add('hidden');
          return;
        }

        this.#continueLoading(productId, recommendationsPerformed, sectionId, intent, id);
      });
  }

  #continueLoading(productId, recommendationsPerformed, sectionId, intent, id, afterAddToCart = false) {
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        const recommendedAnchor = afterAddToCart ? null : document.querySelector('[data-main-product-recommended-anchor]');

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;

          window.SN?.ModulesLoader?.modules && window.SN?.ModulesLoader?.modules(this, '[data-module-name]');
          document.dispatchEvent(new CustomEvent('sn:productRecommendation:render'));

          recommendedAnchor?.classList.remove('d-none');
        } else {
          this.#handleError(new Error('No recommendations available'), true);
          recommendedAnchor?.remove();
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetch + cache
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = window.ProductRecommendations.cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });

      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      window.ProductRecommendations.cachedRecommendations[url] = text;

      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Error handler
   */
  #handleError(error, onlyWarning = false) {
    const fn = onlyWarning ? 'warn' : 'error';
    console[fn]('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
